
const debounceMap = new Map();
const debounceTime = 200;

/**
 * Transforms `func` into a debounced version of itself. See `debounce` for more details.
 * @template {(...args: any[]) => any} T
 * @param {T} func 
 * @param {number|string} [wait=200]
 * @param {import("./defs").DebounceOptions} [options={}]
 * @returns {T}
 */
export function debounced(func, wait = debounceTime, options = {}) {
	const delay = Number(wait);
	if (isNaN(delay)) return /** @type {T} */ (() => {});

	const { leading = false, key } = options;

	return /** @type {T} */ ((...args) => {
		const mapKey = key ?? func;
		let timer = debounceMap.get(mapKey);

		if (leading) {
			if (!timer) {
				// @ts-ignore
				func.apply(this, args);
				timer = setTimeout(() => {
					debounceMap.delete(mapKey);
				}, delay);
				debounceMap.set(mapKey, timer);
			}
		} else {
			clearTimeout(timer);
			timer = setTimeout(() => {
				// @ts-ignore
				func.apply(this, args);
				debounceMap.delete(mapKey);
			}, delay);
			debounceMap.set(mapKey, timer);
		}
	});
}

/**
 * Delays execution of the `func` until a timeout has elapsed. If the debounce for the func is executed again before the timer expires, the timer is reset.
 * @param {() => void} func 
 * @param {number|string} [wait=200]
 * @param {import("./defs").DebounceOptions} [options={}]
 * @returns {void}
 */
export function debounce(func, wait = debounceTime, options={}) {
	debounced(func, wait, options)();
}
